/**
 * Admin Feature Flags page
 *
 * DB-backed allowlist for gated workflows (interactive-edu, kubicle, ...).
 * Grant a feature to an individual user (by email), a whole team, or a whole
 * org. Resolved server-side and delivered on GET /api/me — no code change or
 * redeploy needed to give someone access.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Trash2, Plus } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';

export const Route = createFileRoute('/admin/features')({
  component: AdminFeatures,
});

type SubjectType = 'USER' | 'TEAM' | 'ORG';

interface FeatureDef { key: string; label: string; description: string }
interface Grant {
  id: string;
  feature: string;
  subjectType: SubjectType;
  subjectId: string;
  subjectLabel: string;
  enabled: boolean;
  dailyLimit: number | null;
  expiresAt: string | null;
  notes: string | null;
  createdAt: string;
}
interface OrgRow { id: string; name: string; slug: string }
interface OrgTeam { id: string; name: string }

function AdminFeatures() {
  const [catalog, setCatalog] = useState<FeatureDef[]>([]);
  const [feature, setFeature] = useState<string>('interactive-edu');
  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add-grant form
  const [subjectType, setSubjectType] = useState<SubjectType>('USER');
  const [email, setEmail] = useState('');
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [orgTeams, setOrgTeams] = useState<OrgTeam[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  async function loadCatalog() {
    try {
      const r = await api.get('/admin/features/catalog');
      setCatalog(r.data.features);
    } catch (e) { console.error('Failed to load feature catalog', e); }
  }

  async function loadGrants(f: string) {
    setLoading(true);
    try {
      const r = await api.get('/admin/features', { params: { feature: f } });
      setGrants(r.data.grants);
    } catch (e) {
      console.error('Failed to load grants', e);
    } finally {
      setLoading(false);
    }
  }

  async function loadOrgs() {
    try {
      const r = await api.get('/admin/organizations');
      setOrgs(r.data.organizations);
    } catch (e) { console.error('Failed to load orgs', e); }
  }

  useEffect(() => { loadCatalog(); loadOrgs(); }, []);
  useEffect(() => { loadGrants(feature); }, [feature]);

  // When picking a team subject, load the chosen org's teams.
  useEffect(() => {
    if (subjectType !== 'TEAM' || !selectedOrgId) { setOrgTeams([]); return; }
    let cancelled = false;
    api.get(`/admin/organizations/${selectedOrgId}`)
      .then((r) => { if (!cancelled) setOrgTeams(r.data.organization.teams.map((t: OrgTeam) => ({ id: t.id, name: t.name }))); })
      .catch((e) => console.error('Failed to load org teams', e));
    return () => { cancelled = true; };
  }, [subjectType, selectedOrgId]);

  const selectedFeatureDef = useMemo(() => catalog.find((c) => c.key === feature), [catalog, feature]);

  async function handleAdd() {
    setError(null);
    const body: Record<string, unknown> = { feature, subjectType };
    if (subjectType === 'USER') {
      if (!email.trim()) { setError('Enter an email'); return; }
      body.email = email.trim();
    } else if (subjectType === 'ORG') {
      if (!selectedOrgId) { setError('Pick an organization'); return; }
      body.subjectId = selectedOrgId;
    } else {
      if (!selectedTeamId) { setError('Pick a team'); return; }
      body.subjectId = selectedTeamId;
    }
    if (notes.trim()) body.notes = notes.trim();

    setBusy(true);
    try {
      await api.post('/admin/features', body);
      setEmail(''); setNotes(''); setSelectedTeamId('');
      await loadGrants(feature);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to add grant');
    } finally {
      setBusy(false);
    }
  }

  // Metered features carry a per-subject ceiling. Saving it is its own request
  // rather than a re-grant, so grantedAt and the audit trail stay untouched.
  const [savingLimit, setSavingLimit] = useState<string | null>(null);

  async function saveLimit(g: Grant, raw: string) {
    const trimmed = raw.trim();
    const next = trimmed === '' ? null : Number(trimmed);
    if (next !== null && (!Number.isInteger(next) || next < 0)) {
      setError('Daily limit must be a whole number of clips, or blank to use the default.');
      return;
    }
    if ((g.dailyLimit ?? null) === next) return;
    setSavingLimit(g.id);
    setError(null);
    try {
      await api.patch(`/admin/features/${g.id}/limit`, { dailyLimit: next });
      await loadGrants(feature);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not save the daily limit');
    } finally {
      setSavingLimit(null);
    }
  }

  async function handleRemove(g: Grant) {
    if (!confirm(`Remove ${feature} access for ${g.subjectType.toLowerCase()} "${g.subjectLabel}"?`)) return;
    try {
      await api.delete(`/admin/features/${g.id}`);
      await loadGrants(feature);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to remove grant');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Feature Flags</h1>
          <p className="text-muted-foreground">
            Grant gated workflows to a user, team, or org. Takes effect on the user's next page load — no deploy.
          </p>
        </div>
        <Button variant="outline" onClick={() => loadGrants(feature)} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Feature selector */}
      <div className="flex items-center gap-2 flex-wrap">
        {catalog.map((c) => (
          <Button
            key={c.key}
            variant={c.key === feature ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFeature(c.key)}
          >
            {c.label}
          </Button>
        ))}
      </div>
      {selectedFeatureDef && (
        <p className="text-sm text-muted-foreground -mt-3">{selectedFeatureDef.description}</p>
      )}

      {error && (
        <div className="rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Add grant */}
      <Card>
        <CardHeader><CardTitle>Grant access</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label>Subject</Label>
              <select
                className="block mt-1 rounded border bg-background px-3 py-2 text-sm"
                value={subjectType}
                onChange={(e) => setSubjectType(e.target.value as SubjectType)}
              >
                <option value="USER">User (email)</option>
                <option value="TEAM">Team</option>
                <option value="ORG">Organization</option>
              </select>
            </div>

            {subjectType === 'USER' && (
              <div className="flex-1 min-w-[220px]">
                <Label>Email</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@company.com" />
              </div>
            )}

            {subjectType === 'ORG' && (
              <div className="flex-1 min-w-[220px]">
                <Label>Organization</Label>
                <select
                  className="block w-full mt-1 rounded border bg-background px-3 py-2 text-sm"
                  value={selectedOrgId}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                >
                  <option value="">Select…</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            )}

            {subjectType === 'TEAM' && (
              <>
                <div className="min-w-[200px]">
                  <Label>Organization</Label>
                  <select
                    className="block w-full mt-1 rounded border bg-background px-3 py-2 text-sm"
                    value={selectedOrgId}
                    onChange={(e) => { setSelectedOrgId(e.target.value); setSelectedTeamId(''); }}
                  >
                    <option value="">Select…</option>
                    {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </div>
                <div className="min-w-[200px]">
                  <Label>Team</Label>
                  <select
                    className="block w-full mt-1 rounded border bg-background px-3 py-2 text-sm"
                    value={selectedTeamId}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                    disabled={!selectedOrgId}
                  >
                    <option value="">Select…</option>
                    {orgTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </>
            )}

            <div className="flex-1 min-w-[180px]">
              <Label>Notes (optional)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. customer pilot" />
            </div>

            <Button onClick={handleAdd} disabled={busy}>
              <Plus className="h-4 w-4 mr-1" /> Grant
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Grants table */}
      <Card>
        <CardHeader><CardTitle>Who has “{selectedFeatureDef?.label ?? feature}”</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center min-h-[160px]">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Daily limit</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grants.map((g) => (
                  <TableRow key={g.id}>
                    <TableCell className="font-medium">{g.subjectLabel}</TableCell>
                    <TableCell><Badge variant="outline">{g.subjectType}</Badge></TableCell>
                    <TableCell>
                      <Badge variant={g.enabled ? 'default' : 'secondary'}>{g.enabled ? 'enabled' : 'disabled'}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          defaultValue={g.dailyLimit ?? ''}
                          placeholder="default"
                          aria-label={`Daily limit for ${g.subjectLabel}`}
                          disabled={savingLimit === g.id}
                          className="h-8 w-24 tabular-nums"
                          onBlur={(e) => saveLimit(g, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {savingLimit === g.id ? 'saving…' : g.dailyLimit == null ? 'default' : 'per day'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {g.expiresAt ? new Date(g.expiresAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{g.notes || '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleRemove(g)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {grants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No grants yet. Add one above.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
